import { defineStore } from 'pinia';
import type {
  CampaignConfig,
  StagedAssets,
  LocationConfig,
  VoucherConfig,
  BackgroundScreenKey,
  ScreenAppearanceKey,
  ScreenBrandColorMap,
  ScreenColorKey,
} from '@/types/form.types';
import { DEFAULT_BUTTON_CONFIGS } from '@/utils/buttonStyles';
import type { CampaignBoundary, CampaignExperience, CampaignFeature, CampaignService } from '@/types/campaign.types';
import type { CampaignSchedule } from '@/components/CampaignScheduler.vue';
import { splitPhoneNumber } from '@/utils/countryDialCodes';
import { IR_DEFAULT_CONFIG, getDefaultWinMessage, isDefaultWinMessage } from '@/config/campaignConfigs';

type BackgroundAssetSlot = 'registrationBackground' | 'prizeBackground';

const createDefaultScreenBrandColors = (): ScreenBrandColorMap => ({
  registration: {
    primary: '#7ae6b1',
    background: '#292e50',
    header: '#181b2f',
    secondary: '#7ae6b1',
    buttonText: '#ffffff',
  },
  interaction: {
    primary: '#7ae6b1',
    background: '#292e50',
    header: '#181b2f',
    secondary: '#7ae6b1',
    buttonText: '#ffffff',
  },
  prize: {
    primary: '#7ae6b1',
    background: '#292e50',
    header: '#181b2f',
    secondary: '#7ae6b1',
    buttonText: '#ffffff',
  },
});

const SCREEN_BACKGROUND_ASSET_KEYS: Record<BackgroundScreenKey, BackgroundAssetSlot> = {
  registration: 'registrationBackground',
  prize: 'prizeBackground',
};

const SCREEN_BACKGROUND_SELECTION_KEYS: Record<BackgroundScreenKey, BackgroundAssetSlot> = {
  registration: 'registrationBackground',
  prize: 'prizeBackground',
};

export const useCampaignBuilderStore = defineStore('campaignBuilder', {
  state: () => ({
    // Template Configuration
    template: {
      boundary: null as import('@/types/campaign.types').CampaignBoundary | null,
      experience: null as import('@/types/campaign.types').CampaignExperience | null,
      experienceType: undefined as string | undefined,
      arTrackingType: 'marker' as import('@/types/campaign.types').ArTrackingType,
      service: 'tap-to-redeem' as CampaignService,
      redeemType: 'Tap to collect',
      voucherIntegration: 'upload-vouchers',
      voucherConfig: {
        mode: 'generated',
        generatedConfig: {
          voucherName: '',
          voucherType: 'single-use',
          validUntil: null,
          codePrefix: '',
          quantity: 25,
          codeLength: 8
        },
        vouchers: []
      } as VoucherConfig,
      quizConfig: IR_DEFAULT_CONFIG.quizConfig,
      ctaButton: undefined,
      buttonConfigs: {
        registration: { ...DEFAULT_BUTTON_CONFIGS.registration },
        interaction: { ...DEFAULT_BUTTON_CONFIGS.interaction },
        prize: { ...DEFAULT_BUTTON_CONFIGS.prize }
      },
      voucherCodes: [] as string[],
      locationConfig: null as LocationConfig | null
    } as CampaignConfig,
    
    // Look & Feel
    lookFeel: {
      brandColors: {
        primary: '#ffffff',
        secondary: '#7ae6b1',
        background: '#292e50',
        header: '#181b2f',
        buttonText: '#ffffff'
      },
      screenBrandColors: createDefaultScreenBrandColors(),
      googleFont: 'Montserrat',
      uploadedAssets: {
        logo: { ...IR_DEFAULT_CONFIG.lookFeel.preBuiltUploadedAssets.logo },
        registrationBackground: { ...IR_DEFAULT_CONFIG.lookFeel.preBuiltUploadedAssets.registrationBackground },
        prizeBackground: { ...IR_DEFAULT_CONFIG.lookFeel.preBuiltUploadedAssets.prizeBackground },
      } as StagedAssets,
      stagedAssets: {} as any, // Files staged locally, not uploaded yet
      selectedDefaultAssets: {
        logo: IR_DEFAULT_CONFIG.lookFeel.selectedDefaultAssets.logo,
        mapMarker: IR_DEFAULT_CONFIG.lookFeel.selectedDefaultAssets.mapMarker,
        ar: IR_DEFAULT_CONFIG.lookFeel.selectedDefaultAssets.ar,
        registrationBackground: IR_DEFAULT_CONFIG.lookFeel.selectedDefaultAssets.registrationBackground,
        prizeBackground: IR_DEFAULT_CONFIG.lookFeel.selectedDefaultAssets.prizeBackground,
      }
    },
    
    // Drop Configuration
    drops: {
      dropsPerDrop: 1,
      maxClaimsPerUser: 3,
      timePeriod: 24,
      winRatio: 50
    },
    
    // Prize Message
    message: {
      text: IR_DEFAULT_CONFIG.message,
      maxLength: 280
    },
    
    // Campaign Schedule
    schedule: {
      startTime: null,
      startDate: null,
      startDateTime: null,
      endTime: null,
      endDate: null,
      endDateTime: null,
      timezone: null,
    } as CampaignSchedule,
    
    // Marker Target (compiled .mind file for MindAR marker tracking)
    markerTarget: {
      sourceImage: null as File | null,
      sourceImagePreview: null as string | null,
      compiledMindFile: null as Blob | null,
      compileProgress: 0,
      compileStatus: 'idle' as 'idle' | 'validating' | 'compiling' | 'uploading-mind' | 'ready' | 'error',
      compileError: null as string | null,
      sourceImageUrl: null as string | null,
      mindFileUrl: null as string | null,
    },

    // User Details
    userDetails: {
      name: '',
      surname: '',
      email: '',
      mobileCountryCode: '+27',
      mobileNumber: '',
      consent: false,
      marketingConsent: false,
      password: '',
      confirmPassword: ''
    },
    
    // Validation State
    validation: {
      template: { error: '', success: '' },
      location: { error: '', success: '' },
      lookFeel: { error: '', success: '' },
      drops: { error: '', success: '' },
      message: { error: '', success: '' },
      schedule: {
        campaignStartDate: { error: '', success: '' },
        campaignStartTime: { error: '', success: '' },
        campaignEndDate: { error: '', success: '' },
        campaignEndTime: { error: '', success: '' },
      },
      userDetails: {
        name: { error: '', success: '' },
        surname: { error: '', success: '' },
        email: { error: '', success: '' },
        mobileCountryCode: { error: '', success: '' },
        mobileNumber: { error: '', success: '' }
      }
    },
    
    // Overall completion state
    completion: {
      template: false,
      location: false,
      lookFeel: false,
      drops: false,
      message: false,
      schedule: false,
      userDetails: false
    }
  }),
  actions: {
    setBoundary(boundary: CampaignBoundary) {
      this.template.boundary = boundary;
    },
    setExperience(experience: CampaignExperience) {
      const currentMessage = this.message.text.trim();
      this.template.experience = experience;
      if (!currentMessage || isDefaultWinMessage(currentMessage)) {
        this.message.text = getDefaultWinMessage(experience);
      }
    },
    setFeature(featureId: CampaignFeature) {
      this.template.feature = featureId;
    },
    setLocationConfig(config: LocationConfig | null) {
      this.template.locationConfig = config;
    },
    setQuizConfig(config: import('@/types/form.types').QuizConfig | null) {
      this.template.quizConfig = config;
    },
    setService(serviceId: CampaignService) {
      this.template.service = serviceId;
    },
    setExperienceType(type: string) {
      this.template.experienceType = type;
    },
    setArTrackingType(type: import('@/types/campaign.types').ArTrackingType) {
      this.template.arTrackingType = type;
    },
    setMarkerSourceImage(file: File | null, preview: string | null) {
      this.markerTarget.sourceImage = file;
      this.markerTarget.sourceImagePreview = preview;
      this.markerTarget.compiledMindFile = null;
      this.markerTarget.compileProgress = 0;
      this.markerTarget.compileStatus = 'idle';
      this.markerTarget.compileError = null;
      this.markerTarget.sourceImageUrl = null;
      this.markerTarget.mindFileUrl = null;
    },
    setMarkerCompileProgress(progress: number) {
      this.markerTarget.compileProgress = progress;
    },
    setMarkerCompiled(blob: Blob) {
      this.markerTarget.compiledMindFile = blob;
      this.markerTarget.compileProgress = 100;
      this.markerTarget.compileError = null;
    },
    setMarkerCompileStatus(status: 'idle' | 'validating' | 'compiling' | 'uploading-mind' | 'ready' | 'error') {
      this.markerTarget.compileStatus = status;
    },
    setMarkerCompileError(error: string) {
      this.markerTarget.compileStatus = 'error';
      this.markerTarget.compileError = error;
    },
    setMarkerSourceImageUrl(url: string | null) {
      this.markerTarget.sourceImageUrl = url;
    },
    setMarkerMindFileUrl(url: string | null) {
      this.markerTarget.mindFileUrl = url;
    },
    clearMarkerTarget() {
      if (this.markerTarget.sourceImagePreview) {
        URL.revokeObjectURL(this.markerTarget.sourceImagePreview);
      }
      this.markerTarget.sourceImage = null;
      this.markerTarget.sourceImagePreview = null;
      this.markerTarget.compiledMindFile = null;
      this.markerTarget.compileProgress = 0;
      this.markerTarget.compileStatus = 'idle';
      this.markerTarget.compileError = null;
      this.markerTarget.sourceImageUrl = null;
      this.markerTarget.mindFileUrl = null;
    },
    setBrandColor(colorType: 'primary' | 'secondary' | 'background' | 'header' | 'buttonText', color: string) {
      this.lookFeel.brandColors[colorType] = color;
    },
    setScreenBrandColor(screen: ScreenAppearanceKey, colorType: ScreenColorKey, color: string) {
      const previousValue = this.lookFeel.screenBrandColors[screen][colorType];
      this.lookFeel.screenBrandColors[screen][colorType] = color;
      if (colorType === 'secondary') {
        this.lookFeel.screenBrandColors[screen].primary = color;
      }

      if (screen === 'registration') {
        this.lookFeel.brandColors[colorType] = color;
        if (colorType === 'secondary') {
          this.lookFeel.brandColors.primary = color;
        }

        // IRL2-2343: cascade registration colours to interaction/prize when they
        // haven't been independently customised (still match the old registration value).
        const dependentScreens: ScreenAppearanceKey[] = ['interaction', 'prize'];
        for (const dep of dependentScreens) {
          if (this.lookFeel.screenBrandColors[dep][colorType] === previousValue) {
            this.lookFeel.screenBrandColors[dep][colorType] = color;
            if (colorType === 'secondary') {
              this.lookFeel.screenBrandColors[dep].primary = color;
            }
          }
        }
      }
    },
    setGoogleFont(font: string) {
      this.lookFeel.googleFont = font;
    },
    stageFile(
      payload: { file: File, staged: boolean, localPreview?: string },
      type: 'logo' | 'mapMarker' | 'ar' | 'background',
      screen: BackgroundScreenKey = 'registration'
    ) {
      const { file, localPreview } = payload;
      const stagedAssetDetails = {
        file,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        localPreview,
        staged: true,
        uploadTimestamp: new Date()
      };
      
      if (type === 'logo') {
        this.lookFeel.selectedDefaultAssets.logo = null;
        this.lookFeel.stagedAssets.logo = stagedAssetDetails;
      } else if (type === 'mapMarker') {
        this.lookFeel.selectedDefaultAssets.mapMarker = null;
        this.lookFeel.stagedAssets.mapMarker = stagedAssetDetails;
      } else if (type === 'ar') {
        this.lookFeel.selectedDefaultAssets.ar = null;
        this.lookFeel.stagedAssets.glbModel = stagedAssetDetails;
      } else if (type === 'background') {
        const selectionKey = SCREEN_BACKGROUND_SELECTION_KEYS[screen];
        const assetKey = SCREEN_BACKGROUND_ASSET_KEYS[screen];
        this.lookFeel.selectedDefaultAssets[selectionKey] = null;
        this.lookFeel.stagedAssets[assetKey] = stagedAssetDetails;
      }
    },
    selectDefaultAsset(asset: any, screen: BackgroundScreenKey = 'registration') {
      if (asset.type === 'logo') {
        if (this.lookFeel.selectedDefaultAssets.logo === asset.id) {
          this.lookFeel.selectedDefaultAssets.logo = null;
          this.lookFeel.uploadedAssets.logo = undefined;
        } else {
          this.lookFeel.stagedAssets.logo = undefined;
          this.lookFeel.selectedDefaultAssets.logo = asset.id;
          this.lookFeel.uploadedAssets.logo = {
            s3Url: asset.s3Url,
            s3Path: asset.s3Path,
            fileName: asset.name,
            bucketType: 'approved',
            fileSize: 0,
            fileType: '',
            uploadTimestamp: new Date(),
          };
        }
      } else if (asset.type === 'mapMarker') {
        if (this.lookFeel.selectedDefaultAssets.mapMarker === asset.id) {
          this.lookFeel.selectedDefaultAssets.mapMarker = null;
          this.lookFeel.uploadedAssets.mapMarker = undefined;
        } else {
          this.lookFeel.stagedAssets.mapMarker = undefined;
          this.lookFeel.selectedDefaultAssets.mapMarker = asset.id;
          this.lookFeel.uploadedAssets.mapMarker = {
            s3Url: asset.s3Url,
            s3Path: asset.s3Path,
            fileName: asset.name,
            bucketType: 'approved',
            fileSize: 0,
            fileType: '',
            uploadTimestamp: new Date(),
          };
        }
      } else if (asset.type === 'ar') {
        if (this.lookFeel.selectedDefaultAssets.ar === asset.id) {
          this.lookFeel.selectedDefaultAssets.ar = null;
          this.lookFeel.uploadedAssets.glbModel = undefined;
        } else {
          this.lookFeel.stagedAssets.glbModel = undefined;
          this.lookFeel.selectedDefaultAssets.ar = asset.id;
          this.lookFeel.uploadedAssets.glbModel = {
            s3Url: asset.s3Url,
            s3Path: asset.s3Path,
            fileName: asset.name,
            bucketType: 'approved',
            fileSize: 0,
            fileType: '',
            uploadTimestamp: new Date(),
          };
        }
      } else if (asset.type === 'background') {
        const selectionKey = SCREEN_BACKGROUND_SELECTION_KEYS[screen];
        const assetKey = SCREEN_BACKGROUND_ASSET_KEYS[screen];

        if (this.lookFeel.selectedDefaultAssets[selectionKey] === asset.id) {
          this.lookFeel.selectedDefaultAssets[selectionKey] = null;
          this.lookFeel.uploadedAssets[assetKey] = undefined;
        } else {
          this.lookFeel.stagedAssets[assetKey] = undefined;
          this.lookFeel.selectedDefaultAssets[selectionKey] = asset.id;
          this.lookFeel.uploadedAssets[assetKey] = {
            s3Url: asset.s3Url,
            s3Path: asset.s3Path,
            fileName: asset.name,
            bucketType: 'approved',
            fileSize: 0,
            fileType: '',
            uploadTimestamp: new Date(),
          };
        }
      }
    },
    updateDropConfig(newDrops: any) {
      Object.assign(this.drops, newDrops);
    },
    updateMessage(newMessage: string) {
      this.message.text = newMessage;
    },
    updateSchedule(newSchedule: any) {
      Object.assign(this.schedule, newSchedule);
    },
    updateUserDetails(newDetails: any) {
      Object.assign(this.userDetails, newDetails);
    },
    syncUserDetails(authUserDetails: any) {
      if (!authUserDetails) return;
      
      const updates: any = {};
      
      // Sync name if empty or if it contains a dot (likely unsanitized username)
      const shouldSyncName = !this.userDetails.name || this.userDetails.name.includes('.');
      if (authUserDetails.name && shouldSyncName && this.userDetails.name !== authUserDetails.name) {
        updates.name = authUserDetails.name;
      }
      
      // Sync surname if empty or if it contains a dot
      const shouldSyncSurname = !this.userDetails.surname || this.userDetails.surname.includes('.');
      if (authUserDetails.surname && shouldSyncSurname && this.userDetails.surname !== authUserDetails.surname) {
        updates.surname = authUserDetails.surname;
      }

      if (authUserDetails.email && !this.userDetails.email) updates.email = authUserDetails.email;
      
      if (authUserDetails.phoneNumber && !this.userDetails.mobileNumber) {
        const { dialCode, nationalNumber } = splitPhoneNumber(authUserDetails.phoneNumber);
        if (dialCode) updates.mobileCountryCode = dialCode;
        if (nationalNumber) updates.mobileNumber = nationalNumber;
      }
      
      if (Object.keys(updates).length > 0) {
        this.updateUserDetails(updates);
      }
    },
    handleTemplateSelection(config: CampaignConfig) {
      Object.assign(this.template, config);
    },
    updateSectionCompletion() {
      this.completion.template = !!(this.template.experienceType);

      // Location completion
      if (this.template.boundary === 'geo') {
        const config = this.template.locationConfig;
        if (config?.type === 'radius') {
          const radiusConfig = config.radiusConfig;
          const hasRadius = typeof radiusConfig?.radius === 'number' && radiusConfig.radius > 0;
          const hasAddress = Boolean(radiusConfig?.address?.trim());
          const hasCoordinates =
            typeof radiusConfig?.lat === 'number' &&
            Number.isFinite(radiusConfig.lat) &&
            typeof radiusConfig?.lng === 'number' &&
            Number.isFinite(radiusConfig.lng);

          this.completion.location = hasRadius && (hasAddress || hasCoordinates);
        } else if (config?.type === 'geojson') {
          this.completion.location = !!(config.geoJsonConfig?.data);
        } else {
          this.completion.location = false;
        }
      } else {
        this.completion.location = true; // Not required for non-location AR
      }

      // Look & Feel completion
      const assetsComplete = true; // Simplified for now
      this.completion.lookFeel = !!(this.lookFeel.brandColors.primary && assetsComplete);

      this.completion.drops = true; // Temporarily set to true

      this.completion.message = !!(this.message.text.length > 0);

      this.completion.schedule = !!(
        this.schedule.startDate &&
        this.schedule.startTime &&
        this.schedule.endDate &&
        this.schedule.endTime
      );

      this.completion.userDetails = !!(this.userDetails.name && this.userDetails.email);
    },
    loadState(savedState: any) {
      const savedHasScreenBrandColors = Boolean(savedState?.lookFeel?.screenBrandColors)
      const isObject = (item: any): item is Record<string, any> => {
        return (item && typeof item === 'object' && !Array.isArray(item));
      }

      const mergeDeep = (target: Record<string, any>, source: Record<string, any>) => {
        for (const key in source) {
          if (isObject(target[key]) && isObject(source[key])) {
            mergeDeep(target[key], source[key]);
          } else {
            target[key] = source[key];
          }
        }
      }

      mergeDeep(this.$state, savedState);

      if (!savedHasScreenBrandColors) {
        const registrationColors = {
          primary: this.lookFeel.brandColors.primary,
          background: this.lookFeel.brandColors.background,
          header: this.lookFeel.brandColors.header,
          secondary: this.lookFeel.brandColors.secondary,
          buttonText: this.lookFeel.brandColors.buttonText,
        };
        this.lookFeel.screenBrandColors = {
          registration: { ...registrationColors },
          interaction: { ...registrationColors },
          prize: { ...registrationColors },
        };
      }

      if (savedState?.lookFeel?.selectedDefaultAssets?.background && !this.lookFeel.selectedDefaultAssets.registrationBackground) {
        this.lookFeel.selectedDefaultAssets.registrationBackground = savedState.lookFeel.selectedDefaultAssets.background;
      }
      if (savedState?.lookFeel?.stagedAssets?.background && !this.lookFeel.stagedAssets.registrationBackground) {
        this.lookFeel.stagedAssets.registrationBackground = savedState.lookFeel.stagedAssets.background;
      }
      if (savedState?.lookFeel?.uploadedAssets?.background && !this.lookFeel.uploadedAssets.registrationBackground) {
        this.lookFeel.uploadedAssets.registrationBackground = savedState.lookFeel.uploadedAssets.background;
      }
    },
    updateUploadedAssets(
      assetType: 'logo' | 'mapMarker' | 'ar' | 'background',
      assetDetails: any,
      screen: BackgroundScreenKey = 'registration'
    ) {
      if (assetType === 'logo') {
        this.lookFeel.uploadedAssets.logo = assetDetails;
      } else if (assetType === 'mapMarker') {
        this.lookFeel.uploadedAssets.mapMarker = assetDetails;
      } else if (assetType === 'ar') {
        this.lookFeel.uploadedAssets.glbModel = assetDetails;
      } else if (assetType === 'background') {
        const assetKey = SCREEN_BACKGROUND_ASSET_KEYS[screen];
        this.lookFeel.uploadedAssets[assetKey] = assetDetails;
      }
    },
  }
});
